import React from 'react';
import WaButton from '../../wa/WaButton';
import {
	FLAG_CONFLICT,
	FLAG_REPEAT,
	LOCATION_LABELS,
	formatDayKeyShort,
} from './schedulingModel';

/**
 * The attention rail — after Generate, exactly what is not clean.
 *
 * Sorted loudest-first by schedulingModel.buildAttentionItems: soft conflicts
 * (red), then unassigned, then rotation repeats (yellow), then open coverage
 * requests. Nothing clean appears here; that is the point.
 */

const TITLES = {
	[FLAG_CONFLICT]: 'Unavailable but assigned',
	[FLAG_REPEAT]: 'Rotation repeat',
	unassigned: 'No teacher assigned',
};

function AttentionRail({
	items,
	coverageRequests,
	teacherNameById,
	cleanCount,
	lockScopeLabel,
	isAdmin,
	busy,
	onLockAllClean,
	onFocusBlock,
	onOpenCoverage,
	loadRows,
}) {
	const flaggedCount = items.length;

	return (
		<aside className='ts-rail' aria-label='Needs attention'>
			<section className='ts-rail__panel'>
				<header className='ts-rail__head'>
					<h2 className='ts-rail__title'>Needs attention</h2>
					<span className={`ts-rail__count${flaggedCount ? ' is-live' : ''}`}>
						{flaggedCount}
					</span>
				</header>

				{flaggedCount === 0 && coverageRequests.length === 0 ? (
					<p className='ts-rail__clear'>
						<i className='fa-solid fa-circle-check' aria-hidden='true' /> Nothing needs a
						decision. {cleanCount} clean {cleanCount === 1 ? 'block' : 'blocks'}.
					</p>
				) : (
					<ul className='ts-rail__list'>
						{items.map(({ block, flag, key }) => (
							<li key={key}>
								<button
									type='button'
									className={`ts-flag ${flag.type === FLAG_CONFLICT ? 'is-conflict' : flag.type === FLAG_REPEAT ? 'is-repeat' : 'is-unassigned'}`}
									onClick={() => onFocusBlock(block)}>
									<span className='ts-flag__title'>
										{TITLES[flag.type] || 'Needs review'} — {formatDayKeyShort(block.dayKey)} ·{' '}
										{LOCATION_LABELS[block.location] || block.location}
									</span>
									<span className='ts-flag__body'>
										{flag.teacherName ? `${flag.teacherName} ` : ''}
										{flag.reasons.join('; ')}
									</span>
								</button>
							</li>
						))}

						{coverageRequests.map((request) => (
							<li key={`coverage-${request.id}`}>
								<button
									type='button'
									className='ts-flag is-coverage'
									onClick={() => onOpenCoverage(request)}>
									<span className='ts-flag__title'>
										Coverage requested — {request.date} ·{' '}
										{LOCATION_LABELS[request.location] || request.location}
									</span>
									<span className='ts-flag__body'>
										{teacherNameById.get(request.teacherId) || request.teacherId}
										{request.reason ? ` — “${request.reason}”` : ''}
									</span>
								</button>
							</li>
						))}
					</ul>
				)}

				{isAdmin && cleanCount > 0 && (
					<WaButton
						className='ts-rail__lockall'
						variant='brand'
						onClick={onLockAllClean}
						disabled={busy}>
						{/* Card note 6: this spans the GENERATED RANGE, not the visible
						    week, matching bulkUpdateScheduleAssignments. The copy says so
						    outright so a multi-week lock is never a surprise. */}
						Lock all {cleanCount} clean · {lockScopeLabel}
					</WaButton>
				)}
			</section>

			<section className='ts-rail__panel'>
				<h2 className='ts-rail__title'>Rotation &amp; load</h2>
				<ul className='ts-load'>
					{loadRows.map(({ teacher, blocks, lessons, cap, overBy, isOverCap }) => {
						const max = Math.max(1, ...loadRows.map((row) => row.blocks));
						return (
							<li className={`ts-load__row${isOverCap ? ' is-over-cap' : ''}`} key={teacher.id}>
								<span className='ts-load__name'>{teacher.name || teacher.id}</span>
								<span className='ts-load__bar' aria-hidden='true'>
									<span
										className={`ts-load__fill${isOverCap ? ' is-over-cap' : ''}`}
										style={{ width: `${Math.round((blocks / max) * 100)}%` }}
									/>
								</span>
								<span className='ts-load__count'>
									{blocks} {blocks === 1 ? 'block' : 'blocks'} · {lessons}{' '}
									{lessons === 1 ? 'lesson' : 'lessons'}
									{/* The cap is SOFT, so a run can legitimately finish over it.
									    Showing the overage is what keeps that honest rather than
									    silent — it was the stated condition for choosing a soft
									    cap over a hard one.

									    ADMIN ONLY. A teacher's lesson cap is a staffing decision
									    about that person, and this rail lists EVERY teacher — so
									    rendering it to a non-admin shows one colleague's cap to
									    another. The route is admin-gated too (AnimatedRoutes,
									    2026-08-30) and that is the real fix; this is the component
									    refusing to leak on its own, so it stays correct if the
									    board ever gains a read-only teacher view. Everything else
									    in this row — load, blocks, lessons — is fine to show. */}
									{isAdmin && cap !== null && (
										<span className={`ts-load__cap${isOverCap ? ' is-over' : ''}`}>
											{isOverCap ? ` · ${overBy} over cap of ${cap}` : ` · cap ${cap}`}
										</span>
									)}
								</span>
							</li>
						);
					})}
				</ul>
			</section>
		</aside>
	);
}

export default AttentionRail;
